import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import { SrOnly } from '@/components/Layout/Parts';
import { ResponsiveTable, Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import { EYTVLinkResource, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';
import { EYTVLinkStatus } from '@/utils/enums';

import CsvExportModal from './CsvExportModal';
import LinksTableRow from './LinksTableRow';

export const CheckboxHolder = styled.div`
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 24px;
  width: 24px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
`;

export const Checkbox = styled.input`
  display: none;
  &:disabled + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.lightGray};
    cursor: not-allowed;
  }

  &:not([disabled]) + ${CheckboxLabel} {
    &:hover {
      border-color: ${({ theme }) => theme.colors.purpleLighter};
    }
  }

  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: '';
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;

const ThCheckbox = styled(Checkbox)`
  &:checked + ${CheckboxLabel} {
    &::after {
      content: '';
      position: absolute;
      left: 8px;
      top: 3px;
      width: 8px;
      height: 14px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 0 0;
      transform: rotate(90deg);
    }
  }
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: center;
    font-weight: 700;

    &:nth-child(1) {
      padding-left: 24px;
      padding-right: 24px;
      width: 60px;
    }

    &:last-child {
      width: 56px;
      position: relative;
    }
  }
  td {
    padding: 24px 12px;

    &:last-child {
      border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
  }
`;

const Wrap = styled.div`
  position: relative;
`;

const ExportVideosButton = styled(Text).attrs({
  as: 'button',
  type: 'button',
  $type: 'caption',
})`
  position: absolute;
  z-index: 10;
  top: -70px;
  left: 0;
  right: 0;
  margin: 0 auto;
  height: 56px;
  width: 224px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border-radius: 100px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const VideosListing = () => {
  const [checkedLinks, setCheckedLinks] = useState(new Set<number>());
  const [showExportModal, setShowExportModal] = useState(false);

  const fetchLinks = ({ pageParam = `/v1/eytv-links` }) => {
    return api.get<never, PaginatedResponse<EYTVLinkResource>>(pageParam);
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<EYTVLinkResource>>(
    ['eytv_links'],
    fetchLinks,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const links = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<EYTVLinkResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  const onCheckAll = () => {
    setCheckedLinks(
      (prev) =>
        new Set(
          prev.size
            ? []
            : links
                ?.filter((l) => l.status === EYTVLinkStatus.DONE)
                ?.map((s) => s.id) ?? [],
        ),
    );
  };

  const onCheck = (id: number) => {
    setCheckedLinks((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  return (
    <Wrap>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                <CheckboxHolder>
                  <ThCheckbox
                    id={`all-links`}
                    type="checkbox"
                    checked={
                      checkedLinks.size ===
                      links?.filter((l) => l.status === EYTVLinkStatus.DONE)
                        ?.length
                    }
                    onChange={onCheckAll}
                  />
                  <CheckboxLabel htmlFor={`all-links`} />
                </CheckboxHolder>
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Trim Time</Th>
              <Th>Links</Th>
              <Th>Date Added</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={6}
              isError={isError}
              hasData={!!links?.length}
            >
              {links.map((link) => (
                <LinksTableRow
                  key={link.id}
                  link={link}
                  onCheck={() => onCheck(link.id)}
                  checked={checkedLinks.has(link.id)}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      {!!checkedLinks.size && (
        <ExportVideosButton
          onClick={() => setShowExportModal(true)}
          as="button"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.2102 14.7692H1.84976C1.68655 14.7692 1.53002 14.7043 1.41462 14.5889C1.29921 14.4735 1.23438 14.317 1.23438 14.1538V1.8461C1.23438 1.68289 1.29921 1.52636 1.41462 1.41095C1.53002 1.29555 1.68655 1.23071 1.84976 1.23071H14.1575C14.3207 1.23071 14.4772 1.29555 14.5926 1.41095C14.708 1.52636 14.7728 1.68289 14.7728 1.8461V10.2066C14.7728 10.2874 14.7569 10.3674 14.726 10.4421C14.6951 10.5167 14.6497 10.5846 14.5926 10.6417L10.6454 14.5889C10.5882 14.6461 10.5204 14.6914 10.4457 14.7223C10.3711 14.7533 10.2911 14.7692 10.2102 14.7692V14.7692Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.7171 10.4609H10.4648V14.7136"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Make selected as CSV
        </ExportVideosButton>
      )}
      <AnimatePresence>
        {showExportModal && (
          <CsvExportModal
            onSuccess={() => setCheckedLinks(new Set())}
            onClose={() => setShowExportModal(false)}
            links={Array.from(checkedLinks)}
          />
        )}
      </AnimatePresence>
    </Wrap>
  );
};

export default VideosListing;
